// crewbit-admin/src/components/money/CancelInvoiceModal.tsx
//
// Centered confirm modal for `cancel_invoice`. Optional reason. Mirrors
// ConfirmDialog overlay shape with one input field.

import { useState } from 'react';
import { XCircle } from 'lucide-react';
import { palette, spacing, typography, transition } from '../../styles/tokens';
import { cancelInvoice } from '../../lib/invoiceMutations';

interface Props {
  isOpen: boolean;
  invoiceId: string;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
}

export function CancelInvoiceModal({ isOpen, invoiceId, onClose, onResult }: Props) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const onConfirm = async () => {
    setLoading(true);
    const trimmed = reason.trim();
    const result = await cancelInvoice(invoiceId, trimmed || 'Cancelled by operator');
    setLoading(false);
    if (result.success) {
      onResult(true, result.data.already_cancelled ? 'Invoice already cancelled.' : 'Invoice cancelled.');
      onClose();
      setReason('');
    } else {
      onResult(false, result.error.message ?? 'Failed to cancel invoice.');
    }
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 420, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.redWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <XCircle size={22} color={palette.red} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Cancel invoice?
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Cancelled invoices cannot be reactivated. The reason is recorded in the activity log.
        </div>

        <label style={{ display: 'block', fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="e.g. Client cancelled the job"
          rows={3}
          style={{
            width: '100%', padding: spacing.sm, borderRadius: 8,
            border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
            fontSize: typography.size.caption, color: palette.g800,
            resize: 'vertical', boxSizing: 'border-box',
          }}
        />

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={onClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Keep invoice</button>
          <button onClick={onConfirm} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: loading ? palette.g300 : palette.red, color: palette.white,
            fontSize: 13.5, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Cancelling…' : 'Cancel invoice'}</button>
        </div>
      </div>
    </>
  );
}
